import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { filterOptionsForDatalist } from '../data/exhibitionLocations';

/**
 * Type-to-search input with a visible suggestion list (replaces unreliable native datalist).
 */
export default function AdminLocationCombobox({
  id,
  label,
  required = false,
  value = '',
  onChange,
  onBlur,
  options = [],
  placeholder = '',
  disabled = false,
  hint = '',
}) {
  const listboxId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const filteredOptions = useMemo(
    () => filterOptionsForDatalist(options, value),
    [options, value],
  );

  const showMenu = open && !disabled && filteredOptions.length > 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [value, filteredOptions.length]);

  const selectOption = (option) => {
    onChange(option);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleBlur = (ev) => {
    window.setTimeout(() => {
      if (rootRef.current?.contains(document.activeElement)) return;
      setOpen(false);
      onBlur?.(ev);
    }, 0);
  };

  const handleKeyDown = (ev) => {
    if (!showMenu) {
      if (ev.key === 'ArrowDown' && filteredOptions.length) {
        ev.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setActiveIndex((idx) => Math.min(idx + 1, filteredOptions.length - 1));
      return;
    }

    if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActiveIndex((idx) => Math.max(idx - 1, 0));
      return;
    }

    if (ev.key === 'Enter' && activeIndex >= 0) {
      ev.preventDefault();
      selectOption(filteredOptions[activeIndex]);
      return;
    }

    if (ev.key === 'Escape') {
      ev.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="admin-location-combobox" ref={rootRef}>
      {label ? (
        <label className="form-label small" htmlFor={id}>
          {label} {required ? <span className="text-danger">*</span> : null}
        </label>
      ) : null}
      <input
        id={id}
        required={required}
        className="form-control form-control-sm"
        value={value}
        onChange={(ev) => {
          onChange(ev.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={showMenu}
        aria-controls={listboxId}
        aria-autocomplete="list"
      />
      {showMenu ? (
        <ul id={listboxId} className="admin-location-combobox__menu" role="listbox">
          {filteredOptions.map((option, index) => (
            <li key={option} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={`admin-location-combobox__option${
                  index === activeIndex ? ' admin-location-combobox__option--active' : ''
                }`}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => selectOption(option)}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {hint ? <p className="form-text small text-muted mb-0 mt-1">{hint}</p> : null}
    </div>
  );
}
